import { useMutation, useQuery } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import { useState } from 'react'
import { PublicFrame } from '../../components/Shell'
import { Turnstile } from '../../components/Turnstile'
import { ErrorNote, Field, Spinner } from '../../components/ui'
import { api } from '../../lib/api'
import { fmtMoney, rewardText } from '../../lib/format'
import { useT } from '../../lib/i18n'
import { useMe } from '../../lib/session'
import { type Discount, discountText } from '../owner/Groups'

interface PublicProgram {
  program: {
    id: string
    name: string
    website: string
    description: string | null
    currency: string
    brandColor: string | null
    autoApprove: boolean
  }
  terms: {
    rewards: { event: string; type: string; bps?: number; amountMinor?: number }[]
    holdDays: number
    minPayoutMinor: number
  }
  standards: { rewardDurationMonths: number; cookieDays: number }
  group: {
    name: string
    slug: string
    discount: Discount | null
    lander: { headline?: string | null; body?: string | null } | null
    questions: { id: string; label: string; required: boolean }[]
  } | null
}

function Questions({
  questions,
  answers,
  onChange,
}: {
  questions: NonNullable<PublicProgram['group']>['questions']
  answers: Record<string, string>
  onChange: (a: Record<string, string>) => void
}) {
  return (
    <>
      {questions.map((q) => (
        <Field key={q.id} label={q.label}>
          <input
            className="input"
            required={q.required}
            maxLength={1000}
            value={answers[q.id] ?? ''}
            onChange={(e) => onChange({ ...answers, [q.id]: e.target.value })}
          />
        </Field>
      ))}
    </>
  )
}

export function JoinPage() {
  const { slug } = useParams({ strict: false }) as { slug: string }
  const { t, locale } = useT()
  const me = useMe()
  const groupSlug = new URLSearchParams(location.search).get('group') ?? undefined
  const q = useQuery({
    queryKey: ['public', slug, groupSlug],
    queryFn: () =>
      api<PublicProgram>(
        `/api/public/programs/${slug}${groupSlug ? `?group=${encodeURIComponent(groupSlug)}` : ''}`,
      ),
  })
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const cfg = useQuery({
    queryKey: ['config'],
    queryFn: () => api<{ turnstileSiteKey: string | null }>('/api/config'),
  })
  const [captcha, setCaptcha] = useState<string | null>(null)
  const [captchaReset, setCaptchaReset] = useState(0)
  const [form, setForm] = useState({ name: '', email: '', country: '', website: '', message: '' })
  const apply = useMutation({
    mutationFn: () =>
      api<{ status: string }>(`/api/public/programs/${slug}/apply`, {
        body: {
          name: form.name,
          email: form.email,
          country: form.country.toUpperCase(),
          website: form.website || null,
          message: form.message || null,
          ...(q.data?.group && { group: q.data.group.slug, answers }),
          turnstileToken: captcha ?? undefined,
        },
      }),
    onError: () => setCaptchaReset((n) => n + 1),
  })
  const join = useMutation({
    mutationFn: () =>
      api<{ status: string }>(`/api/partner/join/${slug}`, {
        body: q.data?.group ? { group: q.data.group.slug, answers } : {},
      }),
  })
  if (q.isLoading) return <Spinner />
  if (q.error || !q.data)
    return (
      <PublicFrame>
        <div className="p-16 text-center text-mute">404</div>
      </PublicFrame>
    )
  const { program, terms, standards, group } = q.data
  const sale = terms.rewards.find((r) => r.event === 'sale') ?? terms.rewards[0]
  const accent = program.brandColor ?? '#ff7a17'
  const done = apply.data ?? join.data

  return (
    <PublicFrame>
      <div className="mx-auto grid max-w-[1200px] gap-12 px-4 py-14 md:grid-cols-[1.1fr_1fr] md:px-12 md:py-20">
        <div>
          <div className="eyebrow-ink mb-6 flex items-center gap-2">
            <span className="h-2 w-2 rounded-full" style={{ background: accent }} />
            {t('programPage')}
          </div>
          <h1 className="display mb-6 text-[44px] md:text-[72px]">{program.name}</h1>
          <a href={program.website} className="mono text-mute" target="_blank" rel="noreferrer noopener">
            {program.website.replace(/^https?:\/\//, '')} ↗
          </a>
          {group?.lander?.headline && <p className="h2 mt-6 max-w-xl">{group.lander.headline}</p>}
          {(group?.lander?.body ?? program.description) && (
            <p className="mt-6 max-w-xl whitespace-pre-line text-lg text-body">
              {group?.lander?.body ?? program.description}
            </p>
          )}
          {sale && (
            <div className="card card-pad mt-10">
              <div className="eyebrow mb-3">{t('youEarn')}</div>
              <div className="display text-[56px]">{rewardText(sale, program.currency, locale)}</div>
              <div className="mt-2 text-body">{sale.event === 'sale' ? t('perSale') : t('fixedPerLead')}</div>
              <div className="mt-6 flex flex-wrap gap-2">
                {group && <span className="badge">{group.name}</span>}
                {group?.discount && (
                  <span className="badge">
                    {t('customerDiscount')}: {discountText(group.discount, program.currency, locale)}
                  </span>
                )}
                <span className="badge">{t('forMonths', { months: standards.rewardDurationMonths })}</span>
                <span className="badge">{t('cookieDays', { days: standards.cookieDays })}</span>
                <span className="badge">
                  {t('payoutsFrom', { amount: fmtMoney(terms.minPayoutMinor, program.currency, locale) })}
                </span>
              </div>
            </div>
          )}
        </div>
        <div className="card card-pad self-start md:p-10">
          {done ? (
            <div aria-live="polite">
              <h2 className="h2 mb-3">
                {done.status === 'approved' || done.status === 'exists' ? t('joined') : t('applyDone')}
              </h2>
              <p className="mb-6 text-body">{t('applyDoneText')}</p>
              <a
                className="btn btn-primary"
                href={
                  me.data?.user ? '/partner' : `/login?next=/partner&email=${encodeURIComponent(form.email)}`
                }
              >
                {t('partnerDashboard')} →
              </a>
            </div>
          ) : me.data?.partner ? (
            <div>
              <h2 className="h2 mb-6">{t('joinProgram')}</h2>
              <form
                className="grid gap-4"
                onSubmit={(e) => {
                  e.preventDefault()
                  join.mutate()
                }}
              >
                {group && <Questions questions={group.questions} answers={answers} onChange={setAnswers} />}
                <button type="submit" className="btn btn-primary w-full" disabled={join.isPending}>
                  {t('joinProgram')}
                </button>
              </form>
              <ErrorNote error={join.error} />
            </div>
          ) : (
            <form
              className="grid gap-5"
              onSubmit={(e) => {
                e.preventDefault()
                apply.mutate()
              }}
            >
              <h2 className="h2">{t('applyTitle')}</h2>
              <Field label={t('name')}>
                <input
                  className="input"
                  required
                  maxLength={80}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </Field>
              <Field label={t('email')}>
                <input
                  className="input"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </Field>
              <div className="grid grid-cols-[120px_1fr] gap-4">
                <Field label={t('country')}>
                  <input
                    className="input uppercase"
                    required
                    pattern="[A-Za-z]{2}"
                    maxLength={2}
                    placeholder="US"
                    value={form.country}
                    onChange={(e) => setForm({ ...form, country: e.target.value })}
                  />
                </Field>
                <Field label={t('website')}>
                  <input
                    className="input"
                    type="url"
                    placeholder="https://"
                    value={form.website}
                    onChange={(e) => setForm({ ...form, website: e.target.value })}
                  />
                </Field>
              </div>
              <Field label={t('message')}>
                <textarea
                  className="textarea"
                  rows={3}
                  maxLength={1000}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                />
              </Field>
              {group && <Questions questions={group.questions} answers={answers} onChange={setAnswers} />}
              <Turnstile siteKey={cfg.data?.turnstileSiteKey} onToken={setCaptcha} resetKey={captchaReset} />
              <button
                type="submit"
                className="btn btn-primary mt-6"
                disabled={apply.isPending || (!!cfg.data?.turnstileSiteKey && !captcha)}
              >
                {t('joinProgram')}
              </button>
              <ErrorNote error={apply.error} />
            </form>
          )}
        </div>
      </div>
    </PublicFrame>
  )
}
